import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingCart, ArrowRight, Truck, ShieldCheck, Lock } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { useCart } from "@/context/CartContext";
import { api, formatUSD } from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again or call us.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="cart-page">
      <PageHeader
        eyebrow="Your Cart"
        title={<>Almost <span className="text-brand-red">protected.</span></>}
        sub="Free US shipping on every order. No long-term contract, cancel any time."
      />

      <section className="py-16 lg:py-24 bg-brand-bg min-h-[40vh]">
        <div className="container-x">
          {count === 0 ? (
            <div className="rounded-3xl bg-white border border-brand-line p-12 text-center max-w-xl mx-auto" data-testid="cart-empty">
              <span className="grid place-items-center w-20 h-20 rounded-full bg-brand-bg mx-auto">
                <ShoppingCart className="w-10 h-10 text-brand-navy" />
              </span>
              <h3 className="mt-6 font-display font-bold text-2xl text-brand-navy">Your cart is empty</h3>
              <p className="mt-3 text-brand-ink2">Explore our medical alert devices and find the right fit.</p>
              <Link
                to="/shop"
                data-testid="cart-shop-link"
                className="btn-focus mt-7 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors"
              >
                Browse devices <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
              {/* items */}
              <div className="space-y-4">
                {items.map((i) => (
                  <div
                    key={i.lookup_key}
                    data-testid={`cart-item-${i.lookup_key}`}
                    className="flex gap-4 sm:gap-6 rounded-3xl bg-white border border-brand-line p-4 sm:p-6"
                  >
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-[radial-gradient(circle_at_50%_30%,#fff,#EDF1F6)] shrink-0 grid place-items-center overflow-hidden">
                      <img src={i.image} alt={i.name} className="w-full h-full object-contain p-2" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-bold uppercase tracking-wide text-brand-red">{i.category}</span>
                      <h3 className="font-display font-bold text-xl text-brand-navy leading-tight">{i.name}</h3>
                      <p className="mt-1 font-bold text-brand-navy">{formatUSD(i.price)}</p>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="inline-flex items-center rounded-full border border-brand-line">
                          <button
                            onClick={() => updateQty(i.lookup_key, i.quantity - 1)}
                            data-testid={`cart-decrease-${i.lookup_key}`}
                            aria-label="Decrease quantity"
                            className="btn-focus grid place-items-center w-11 h-11 rounded-full text-brand-navy hover:text-brand-red"
                          >
                            <Minus className="w-4 h-4" strokeWidth={2.6} />
                          </button>
                          <span className="w-8 text-center font-bold text-brand-navy" data-testid={`cart-qty-${i.lookup_key}`}>
                            {i.quantity}
                          </span>
                          <button
                            onClick={() => updateQty(i.lookup_key, i.quantity + 1)}
                            data-testid={`cart-increase-${i.lookup_key}`}
                            aria-label="Increase quantity"
                            className="btn-focus grid place-items-center w-11 h-11 rounded-full text-brand-navy hover:text-brand-red"
                          >
                            <Plus className="w-4 h-4" strokeWidth={2.6} />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(i.lookup_key)}
                          data-testid={`cart-remove-${i.lookup_key}`}
                          className="btn-focus inline-flex items-center gap-1.5 text-brand-ink2 hover:text-brand-red font-semibold"
                        >
                          <Trash2 className="w-4 h-4" /> Remove
                        </button>
                      </div>
                    </div>
                    <div className="hidden sm:block text-right">
                      <span className="font-display font-bold text-xl text-brand-navy">
                        {formatUSD(i.price * i.quantity)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* summary */}
              <div className="rounded-3xl bg-white border border-brand-line shadow-card p-7 lg:sticky lg:top-28">
                <h3 className="font-display font-bold text-2xl text-brand-navy">Order summary</h3>
                <div className="mt-6 space-y-3 text-brand-navy">
                  <div className="flex justify-between">
                    <span className="text-brand-ink2">Subtotal ({count} item{count > 1 ? "s" : ""})</span>
                    <span className="font-bold" data-testid="cart-subtotal">{formatUSD(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-ink2">Shipping</span>
                    <span className="font-bold text-emerald-600">FREE</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-brand-ink2">Sales tax</span>
                    <span className="text-brand-ink2">Calculated at checkout</span>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-brand-line flex justify-between items-end">
                  <span className="font-bold text-brand-navy text-lg">Total</span>
                  <span className="font-display font-black text-3xl text-brand-navy" data-testid="cart-total">
                    {formatUSD(subtotal)}
                  </span>
                </div>

                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="checkout-button"
                  className="btn-focus mt-6 w-full inline-flex items-center justify-center gap-2.5 h-15 min-h-[60px] rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors shadow-red disabled:opacity-60"
                >
                  {loading ? "Redirecting..." : <><Lock className="w-5 h-5" /> Secure checkout</>}
                </button>

                <div className="mt-6 space-y-2.5 text-sm text-brand-ink2">
                  <p className="flex items-center gap-2"><Truck className="w-4 h-4 text-brand-red" /> Free US shipping, always</p>
                  <p className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-red" /> Secure payment via Stripe</p>
                  <p className="flex items-center gap-2"><Lock className="w-4 h-4 text-brand-red" /> No long-term contract</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
